import { gql, useApolloClient, useMutation } from '@apollo/client';
import { Button, Text } from '@apollo/orbit';
import React, { useEffect } from 'react';
import { Redirect } from 'react-router';
import { Link } from 'react-router-dom';

import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { useIdentity } from 'src/hooks/useIdentity';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { appLinkContext } from 'src/lib/apollo/link';
import {
  inviteErrorMapper,
  isExpiredInviteError,
  isInvalidTokenError,
  isMaxGraphOSMetError,
  isSSOJoinError,
} from 'src/lib/errors/inviteErrorMapper/inviteErrorMapper';
import type { GraphQLTypes } from 'src/lib/graphqlTypes';
import { events } from 'src/lib/tracking';

import { invite, org } from '../routes';

const acceptInvitationMutation = gql<
  GraphQLTypes.UI__AcceptInvitationMutation,
  GraphQLTypes.UI__AcceptInvitationMutationVariables
>/* GraphQL */ `
  mutation UI__AcceptInvitationMutation($orgId: ID!, $joinToken: String!) {
    joinAccount(accountId: $orgId, joinToken: $joinToken) {
      id
      currentPlan {
        id
        tier
      }
    }
  }
`;

export const AccountInvitationHandler = () => {
  const { orgId, joinToken } = useRouteParams(invite);

  // Require the identity, but don't fail if this doesn't go right. That'll be
  // handled elsewhere.
  const identity = useIdentity();
  const client = useApolloClient();

  const [acceptInvitation, { data, error }] = useMutation(
    acceptInvitationMutation,
    {
      onCompleted(mutationData) {
        client.cache.evict({
          id: client.cache.identify({
            __typename: identity.me?.__typename,
            id: identity.me?.id,
          }),
        });

        client.cache.evict({
          id: 'ROOT_QUERY',
          fieldName: 'me',
        });

        client.cache.gc();

        // Track onboarding events for users joining via invite
        const subscriptionTier = mutationData.joinAccount?.currentPlan?.tier;
        const joinedOrgId = mutationData.joinAccount?.id;

        // Find the user's role for the joined org from identity memberships
        // Note: This may be undefined if the membership hasn't been cached yet,
        // but AmplitudeProvider will update user properties when identity refreshes
        const userRole = identity.memberships.find(
          (membership) => membership.account.id === joinedOrgId,
        )?.role;

        /* eslint-disable @typescript-eslint/naming-convention */
        events.identify({
          user_role: userRole,
          active_org_subscription_tier: subscriptionTier,
        });

        events.onboarding_guide_triggered.track({});
        /* eslint-enable @typescript-eslint/naming-convention */
      },
      context: appLinkContext({
        ignoreSentryReporting: [
          isSSOJoinError,
          isInvalidTokenError,
          isMaxGraphOSMetError,
          isExpiredInviteError,
        ],
      }),
    },
  );

  // when first mounted, fire a mutation that accepts the invitation.
  // passing [] as the 2nd arg to useEffect means it should only ever
  // run once (like componentDidMount).
  useEffect(() => {
    acceptInvitation({
      variables: {
        orgId,
        joinToken,
      },
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // if anything goes wrong, fall back to a NotFound page.
  if (error) {
    console.warn(error); // eslint-disable-line no-console
    return (
      <NotFound
        image="astronaut"
        title="Invitation error"
        subtitle={
          <div className="flex flex-col gap-4">
            {inviteErrorMapper(error).map((errorMessage) => (
              <Text key={errorMessage}>{errorMessage}</Text>
            ))}
            <Button as={Link} to="/">
              Go to Studio
            </Button>
          </div>
        }
      />
    );
  }
  // once our mutation has succeeded, redirect to the org
  // that was just joined
  if (data && data.joinAccount)
    return <Redirect to={org.location({ orgId: data.joinAccount.id })} />;

  // while the mutation is running, just show the loading spinner.
  return <Loading />;
};
